import { useCallback } from 'react';

import { Stack } from '@mui/system';
import MenuItem from '@mui/material/MenuItem';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { useTheme } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import { Card, Link, Typography, CardContent, useMediaQuery } from '@mui/material';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';
import { useParams, useRouter } from 'src/routes/hooks';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { useTranslate } from 'src/locales';
import { useAppDispatch } from 'src/store/store';
import { List, ListEntityType } from 'src/services/lists/lists.types';

import Iconify from 'src/components/iconify';
import DateDisplay from 'src/components/date/date-display';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import TruncatedTextField from 'src/components/truncate-text/truncated-text-field';

import { TenantType } from 'src/types/enums';


// ----------------------------------------------------------------------
type Props = {
  row: List,
  onEditList: (data: List) => void;
  onSendMultiEmail: (list: List) => void;
};

export default function ListsTableRow({
  row,
  onEditList,
  onSendMultiEmail
}: Props) {

  const theme = useTheme();

  const tenantType = useOrgTenant();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const popover = usePopover();

  const { t } = useTranslate();

  const router = useRouter();

  const dispatch = useAppDispatch();

  const { target_tenant } = useParams();

  const getViewDetailPath = useCallback((data: List) => paths.dashboard.lists.detail(data.id), []);

  const viewDetail = useCallback((data: List) => {
    router.push(getViewDetailPath(data));
  }, [router, getViewDetailPath]);

  if (isMobile) {
    return (
      <Card sx={{ mx: 2, mb: 1 }}>
        <CardContent sx={{ position: 'relative', pr: 6, cursor: 'pointer' }} >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Stack direction="column" alignItems="start">
              <Link
                href={getViewDetailPath(row)}
                style={{
                  color: theme.palette.text.primary,
                  textDecoration: 'none',
                }}
              >
                <TruncatedTextField
                  text={row.name}
                  mode="characters"
                  limit={25}
                  showMoreEnabled={false}
                  variant="caption"
                  fontWeight="bold"
                  gutterBottom
                  style={{ textTransform: 'capitalize' }}
                />
              </Link>
            </Stack>
          </Stack>

          <IconButton
            sx={{ position: 'absolute', top: 8, right: 8, zIndex: 50 }}
            color={popover.open ? 'primary' : 'default'}
            onClick={popover.onOpen}
          >
            <Iconify icon="eva:more-horizontal-fill" />
          </IconButton>
        </CardContent>

        <CustomPopover
          open={popover.open}
          onClose={popover.onClose}
          arrow="right-top"
          sx={{ width: 140 }}
        >
          <MenuItem
            onClick={() => {
              popover.onClose();
              viewDetail(row);
            }}
          >
            <Iconify icon="solar:eye-bold" />
            {t('common.view')}
          </MenuItem>
        </CustomPopover>
      </Card>
    );
  }

  return (
    <>
      <TableRow hover>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Stack direction="row" alignItems="center" justifyContent="start" spacing={.5}>
            <Link
              component={RouterLink}
              noWrap
              color="inherit"
              href={getViewDetailPath(row)}
            >
              <TruncatedTextField
                text={row.name}
                mode="characters"
                limit={50}
                showMoreEnabled={false}
                variant="subtitle2"
                fontWeight="bold"
                style={{ textTransform: 'capitalize' }}
              />
            </Link>
          </Stack>
        </TableCell>

        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Typography variant="body2" color="text.secondary">
            {row.owner ? `${row.owner?.first_name} ${row.owner?.last_name}` : '--'}
          </Typography>
        </TableCell >

        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Typography variant="body2" color="text.secondary">
            {row.entity_count || t('lists.table.empty')}
          </Typography>
        </TableCell>

        <TableCell>
          <DateDisplay date={row.created_at} format="DD/MM/YYYY hh:mm A" variant='body2' />
        </TableCell>

        <TableCell align="right" sx={{ px: 1, whiteSpace: 'nowrap' }}>
          <IconButton color={popover.open ? 'inherit' : 'default'} onClick={popover.onOpen}>
            <Iconify icon="eva:more-horizontal-fill" />
          </IconButton>
        </TableCell>
      </TableRow >

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        arrow="right-top"
      >
        <MenuItem
          onClick={() => {
            popover.onClose();
            viewDetail(row);
          }}
        >
          <Iconify icon="solar:eye-bold" />
          {t('common.view')}
        </MenuItem>

        <MenuItem
          onClick={() => {
            popover.onClose();
            onEditList(row);
          }}
        >
          <Iconify icon="material-symbols:edit" />
          {t('common.edit')}
        </MenuItem>

        {
          [TenantType.Client, TenantType.Recruiter].includes(tenantType) && [ListEntityType.CANDIDATE, ListEntityType.CONTACT].includes(row.entity_type) && (
            <MenuItem
              onClick={() => {
                popover.onClose();
                onSendMultiEmail?.(row);
              }}
            >
              <Iconify icon="iconoir:send-mail" />
              {t('lists.actions.send_bulk_mail')}
            </MenuItem>
          )
        }

      </CustomPopover>
    </>
  );
}